import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { notification } from 'antd';

import { NOTIFICATION_MESSAGES, QUERY_DEFAULTS } from '@/shared/lib/constants';
import { assetKeys, discoveryKeys, domainKeys } from '@/shared/lib/query/queryKeys';

import {
  buildDomainTree,
  type DomainItem,
  type DomainsResponse,
  type DomainTreeNode,
  fetchDomains,
  updateAssetDomains,
} from '@/features/asset-detail/api/domainsApi';

export const useDomains = () => {
  return useQuery<DomainsResponse>({
    queryKey: domainKeys.domainTree,
    queryFn: fetchDomains,
    staleTime: QUERY_DEFAULTS.STALE_TIME.LONG,
    gcTime: QUERY_DEFAULTS.GC_TIME.LONG,
  });
};

export const useDomainTree = () => {
  const { data, isLoading, error } = useDomains();

  const tree: DomainTreeNode[] = data ? buildDomainTree(data.data) : [];

  return {
    tree,
    domains: data?.data || [],
    isLoading,
    error,
  };
};

export const useUpdateAssetDomains = (
  entityType: string,
  serviceType: string | undefined,
  assetId: string,
  fqn: string
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      selectedDomainId,
      domains,
      currentDomains,
    }: {
      selectedDomainId: string | null;
      domains: DomainItem[];
      currentDomains: Array<{
        id: string;
        type: string;
        name: string;
        fullyQualifiedName: string;
        description?: string;
        displayName?: string;
        inherited?: boolean;
      }>;
    }) => updateAssetDomains(entityType, serviceType, assetId, selectedDomainId, domains, currentDomains),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfo(entityType, fqn) });
      queryClient.invalidateQueries({ queryKey: discoveryKeys.searchPrefix() });
      queryClient.invalidateQueries({ queryKey: assetKeys.dataAssetsPrefix() });

      notification.success({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS,
        description: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_SUCCESS_DESC,
        placement: 'bottomLeft',
        duration: 3,
      });
    },
    onError: (error: unknown) => {
      queryClient.invalidateQueries({ queryKey: assetKeys.assetBaseInfo(entityType, fqn) });
      notification.error({
        message: NOTIFICATION_MESSAGES.DOMAIN_UPDATE_ERROR,
        description: error instanceof Error ? error.message : NOTIFICATION_MESSAGES.DOMAIN_UPDATE_ERROR_DESC,
        placement: 'bottomLeft',
        duration: 5,
      });
    },
  });
};
